import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionItem,
  Autocomplete,
  AutocompleteItem,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  DatePicker,
  DateValue,
  Divider,
  Image,
  Input,
  Progress,
  Radio,
  RadioGroup,
  ScrollShadow,
  Select,
  SelectItem,
} from "@heroui/react";
import { I18nProvider, useDateFormatter, useLocale } from "@react-aria/i18n";
import { useMediaQuery } from "usehooks-ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ChangeEvent, useCallback, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  CheckCircle2,
  Dna,
  FileUp,
  Fingerprint,
  HeartPulse,
  Ruler,
  Scale,
  Syringe,
  Trash2,
  UploadCloud,
  User,
} from "lucide-react";

import { Form } from "../../components/ui/form";
import { registeringAnimal } from "../Animals/animalApiSlice";
import useAuth from "../../hooks/useAuth";
import { useGetAllFarmers, useGetAllFarms } from "@/features/Admin/Admin.api.ts";
import { animalSchema, TAnimalSchema } from "@/models/Animal.Schema.ts";
import { AuthStore } from "@/store/useAuthStore.ts";
import {
  birthConditions,
  breedingTypes,
  coatColor,
  frameSizes,
  nigerianBreeds,
  paternalTypes,
  ratios,
  statuses,
  vaccineArray,
} from "@/utils/items.ts";
import { cn } from "@/lib/utils.ts";
import { storage, auth } from "@/config/firebase.ts";
import { ensureStorageAuthenticated } from "@/config/storageAuth";
import { useEffect, useState } from "react";

interface AnimalFormProps {
  onClose?: () => void;
}

// Storage Keys
const FORM_STORAGE_KEY = "adminAnimalFormData";
const STEP_STORAGE_KEY = "adminAnimalFormStep";
const DATE_STORAGE_KEY = "adminAnimalFormDate";

const AnimalForm = ({ onClose }: AnimalFormProps) => {
  // 1. Initialize Step from LocalStorage
  const [currentStep, setCurrentStep] = useState(() => {
    if (typeof window !== "undefined") {
      const savedStep = localStorage.getItem(STEP_STORAGE_KEY);
      return savedStep ? parseInt(savedStep, 10) : 0;
    }
    return 0;
  });

  const [direction, setDirection] = useState(0); 
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        console.log("AnimalForm Auth State Changed:", user ? "Signed In" : "Signed Out");
        setFirebaseUser(user);
    });
    return () => unsubscribe();
  }, []);

  const [img, setImg] = useState<File>();
  const [preview, setPreview] = useState<string | null>(null);

  // 2. Initialize Date from LocalStorage
  const [value, setValue] = useState<DateValue | null>(() => {
    if (typeof window !== "undefined") {
      const savedDate = localStorage.getItem(DATE_STORAGE_KEY);
      try {
        return savedDate ? parseDate(savedDate) : parseDate("2024-04-04");
      } catch (e) {
        return parseDate("2024-04-04");
      }
    }
    return parseDate("2024-04-04");
  });

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const userAuth = useAuth();
  const id = userAuth?.id;
  const isAdmin = userAuth?.isAdmin;
  const { farmInfo } = AuthStore();

  const { data: farmers } = useGetAllFarmers();
  const { data: allFarms } = useGetAllFarms();

  // 3. Helper to get initial form values
  const getInitialFormValues = (): Partial<TAnimalSchema> => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse saved form data", e);
        }
      }
    }
    return {
      gn: "",
      sex: "",
      name: "",
      weight: "",
      vaccination: "",
      damDGR: "",
      damBreed: "",
      damName: "",
      sireDGR: "",
      sireName: "",
      sireBreed: "",
      age: "",
      paternalGrandDamDGR: "",
      paternalGrandDamName: "",
      paternalGrandDamBreed: "",
      paternalGrandSireDGR: "",
      paternalGrandSireName: "",
      paternalGrandSireBreed: "",
      maternalGrandDamDGR: "",
      maternalGrandDamName: "",
      maternalGrandDamBreed: "",
      maternalGrandSireDGR: "",
      maternalGrandSireName: "",
      maternalGrandSireBreed: "",
      animalStatus: "",
      birthWeight: "",
      coatColor: "",
      size: "",
      birthCondition: "",
      breedingType: "",
      paternalType: "",
      racialComposition: "",
      breedCompositionRatio: "",
      farmerID: "",
      farmID: "",
    };
  };

  const form = useForm<TAnimalSchema>({
    resolver: zodResolver(animalSchema),
    mode: "onChange",
    defaultValues: getInitialFormValues() as TAnimalSchema, // Load saved data immediately
  });

  // 4. Persistence Effects
  
  // Save Form Data
  useEffect(() => {
    const subscription = form.watch((values) => {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Save Step
  useEffect(() => {
    localStorage.setItem(STEP_STORAGE_KEY, currentStep.toString());
  }, [currentStep]);

  // Save Date
  useEffect(() => {
    if (value) {
      localStorage.setItem(DATE_STORAGE_KEY, value.toString());
    }
  }, [value]);


  const { mutateAsync, isError, failureCount, status, isPending } = useMutation({
    mutationKey: ["registerAnimals"],
    mutationFn: async (data: any) => {
      return await registeringAnimal(data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["animals"] });
      const previousAnimals = queryClient.getQueryData<any>(["animals"]);

      queryClient.setQueryData<any>(["animals"], (old: any) => {
        if (!old) return [];
        return [...old, { ...data }];
      });

      return { previousAnimals };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      toast.success("New Animal has been registered successfully");
      
      // Clean up local storage on success
      localStorage.removeItem(FORM_STORAGE_KEY);
      localStorage.removeItem(STEP_STORAGE_KEY);
      localStorage.removeItem(DATE_STORAGE_KEY);
      
      onClose?.();
    },

    onError: useCallback(
      (error: Error, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to Register New Animal. Reverting...");
        queryClient.setQueryData(["animals"], context?.previousAnimals);
      },
      [queryClient],
    ),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
    },
  });

  const handleImageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImg(file);
        setPreview(URL.createObjectURL(file));
      }
    },
    [setImg, setPreview],
  );

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
        setImg(file);
        setPreview(URL.createObjectURL(file));
    } else {
        toast.error("Please drop a valid image file");
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
  }, []);

  const selectedFarmerId = form.watch("farmerID");
  const filteredFarms = (allFarms || []).filter(
    (farm: any) => String(farm.farmOwnerId) === selectedFarmerId,
  );

  const steps = [
    {
      id: "Step 1",
      name: "Identity & Vitals",
      icon: <Fingerprint className="w-4 h-4" />,
      fields: [
        "gn",
        "name",
        "birthDate",
        "age",
        "status",
        "sex",
        "age",
        "vaccination",
        "weight",
        "birthWeight",
        "breedingType",
        "size",
        "coatColor",
        "racialComposition",
        "breedCompositionRatio",
      ],
    },
    {
      id: "Step 2",
      name: "Pedigree Tree",
      icon: <Dna className="w-4 h-4" />,
      fields: [
        "damWeightAtWeaning",
        "damDGR",
        "damName",
        "damBreed",
        "sireDGR",
        "sireName",
        "sireBreed",
        "paternalGrandDamDGR",
        "paternalGrandDamName",
        "paternalGrandDamBreed",
        "paternalGrandSireDGR",
        "paternalGrandSireName",
        "paternalGrandSireBreed",
        "maternalGrandDamDGR",
        "maternalGrandDamName",
        "maternalGrandDamBreed",
        "maternalGrandSireDGR",
        "maternalGrandSireName",
        "maternalGrandSireBreed",
      ],
    },
    {
      id: "Step 3",
      name: "Verification",
      icon: <Camera className="w-4 h-4" />,
      fields: ["paternalType", "birthCondition"],
    },
  ];

  const { locale } = useLocale();
  const formatter = useDateFormatter({ dateStyle: "full" });
  const now = today(getLocalTimeZone());
  const nextWeek = startOfWeek(now.add({ weeks: 1 }), locale);
  const nextMonth = startOfMonth(now.add({ months: 1 }));

  const CustomRadio = (props: any) => {
    const { children, ...otherProps } = props;

    return (
      <Radio
        {...otherProps}
        classNames={{
          base: cn(
            "flex-none m-0 h-8 bg-content1 hover:bg-content2 items-center justify-between",
            "cursor-pointer rounded-full border-2 border-default-200/60",
            "data-[selected=true]:border-primary",
            "transition-all duration-200"
          ),
          label: "text-tiny text-default-500",
          labelWrapper: "px-1 m-0",
          wrapper: "hidden",
        }}
      >
        {children}
      </Radio>
    );
  };

  const onSubmit: SubmitHandler<TAnimalSchema> = async () => {
    try {
      let currentUser = firebaseUser;
      
      // 1. Try direct check first
      if (!currentUser) {
        currentUser = auth.currentUser;
        if (currentUser) {
           setFirebaseUser(currentUser);
        }
      }

      // Restore Blaze-project Storage authentication for existing app sessions.
      if (!currentUser) {
        toast.info("Connecting to storage service...", { duration: 2000 });

        try {
          currentUser = await ensureStorageAuthenticated();
          setFirebaseUser(currentUser);
          toast.success("Connection restored!");
        } catch (authError) {
          console.error("Storage authentication failed:", authError);
          toast.error("Failed to connect to storage service.");
          return;
        }
      }

      console.log("Final Firebase User Check:", currentUser);
      if (!currentUser) {
        toast.error("Authentication error: You are not signed in to Firebase Storage. Please refresh the page and try again.");
        return;
      }

      // Force token refresh to ensure it's valid and active
      try {
        const token = await currentUser.getIdToken(true);
        console.log("Forced token refresh successful. Token prefix:", token.substring(0, 10));
      } catch (tokenError) {
        console.error("Failed to refresh Firebase token:", tokenError);
        toast.error("Authentication error: Failed to refresh your session. Please login again.");
        return;
      }

      const weight = Number(form.getValues().weight);
      const birthWeight = Number(form.getValues().birthWeight);

      const animalData: any = {
        gn:
          "SG" +
          (farmInfo?.farmName ?? "").slice(0, 2) +
          form.getValues().gn,
        birthDate: value
          ? formatter.format(value.toDate(getLocalTimeZone()))
          : "",
        sex: form.getValues().sex,
        name: form.getValues().name,
        age: form.getValues().age,
        size: form.getValues().size,
        weight: weight,
        birthWeight: birthWeight,
        coatColor: form.getValues().coatColor,
        breedingType: form.getValues().breedingType,
        damName: form.getValues().damName,
        damDGR: form.getValues().damDGR,
        damBreed: form.getValues().damBreed,
        sireDGR: form.getValues().sireDGR,
        sireName: form.getValues().sireName,
        sireBreed: form.getValues().sireBreed,
        paternalGrandDamDGR: form.getValues().paternalGrandDamDGR,
        paternalGrandDamName: form.getValues().paternalGrandDamName,
        paternalGrandDamBreed: form.getValues().paternalGrandDamBreed,
        paternalGrandSireDGR: form.getValues().paternalGrandSireDGR,
        paternalGrandSireName: form.getValues().paternalGrandSireName,
        paternalGrandSireBreed: form.getValues().paternalGrandSireBreed,
        maternalGrandDamDGR: form.getValues().maternalGrandDamDGR,
        maternalGrandDamName: form.getValues().maternalGrandDamName,
        maternalGrandDamBreed: form.getValues().maternalGrandDamBreed,
        maternalGrandSireDGR: form.getValues().maternalGrandSireDGR,
        maternalGrandSireName: form.getValues().maternalGrandSireName,
        maternalGrandSireBreed: form.getValues().maternalGrandSireBreed,
        birthCondition: form.getValues().birthCondition,
        paternalType: form.getValues().paternalType,
        racialComposition:
          form.getValues().racialComposition +
          form.getValues().breedCompositionRatio,
        vaccination: form.getValues().vaccination,
        animalStatus: form.getValues().animalStatus,
        farmerID: form.getValues().farmerID || id || "",
        farmID: form.getValues().farmID || farmInfo.id,
        // SGIP Contemporary Group Data (Calculated via backend or batch process)
        cgAdgAvg: "0.00",
        cgAdgDiff: "0.00",
        cgAdgSd: "0.00",
        cgW120Avg: "0.00",
        cgW120Diff: "0.00",
        cgW120Sd: "0.00",
        url: "",
      };

      if (img) {
        const sanitizedName = img.name.replace(/[^a-zA-Z0-9.]/g, "_");
        const fileName = `${Date.now()}_${sanitizedName}`;
        const storageRef = ref(storage, "images/animals/" + fileName);
        const metadata = {
          contentType: img.type,
          customMetadata: {
            farmerID: form.getValues().farmerID || id || "",
            farmID: form.getValues().farmID || farmInfo.id || "",
          },
        };
        const uploadTask = uploadBytesResumable(storageRef, img, metadata);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            () => {
              toast.info("Image Uploading", {
                duration: 2000,
                icon: <UploadCloud className="w-4 h-4 animate-bounce" />,
              });
            },
            (error) => {
              console.error(error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                animalData.url = downloadURL;
                resolve();
              } catch (err) {
                reject(err);
              }
            }
          );
        });
      }

      toast.promise(mutateAsync(animalData), {
        loading: "Registering animal in database...",
        success: "Animal Registered Successfully",
        error: "Failed to register",
        position: "top-right",
      });

      if (isError) {
        console.log(status);
        console.log(failureCount);
      }
    } catch (error) {
      console.log(error);
    }
  };

  type FieldName = keyof TAnimalSchema;

  const next = async () => {
    const fields = steps[currentStep].fields;
    const output = await form.trigger(fields as FieldName[], {
      shouldFocus: true,
    });

    if (!output) return;

    if (currentStep < steps.length - 1) {
      setDirection(1);
      setCurrentStep((step) => step + 1);
    } else if (currentStep === steps.length - 1) {
        await form.handleSubmit(onSubmit)();
    }
  };

  const prev = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep((step) => step - 1);
    }
  };

  // Framer Motion Variants
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0,
    }),
  };

  return (
    <Card  className={cn(
        "mx-auto flex flex-col self-center h-full font-['suse'] bg-background text-foreground top-16",
        isMobile ? "px-2 py-4" : "max-w-4xl min-w-[600px] px-6 py-6"
    )}>
        {/* Header / Stepper */}
        <CardHeader className="mb-6 space-y-2">
            <div className="flex justify-between items-center text-sm font-medium text-default-500">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <span className="text-primary">{Math.round(((currentStep + 1) / steps.length) * 100)}% Completed</span>
            </div>
            <Progress 
                aria-label="Form Progress" 
                size="sm" 
                value={((currentStep + 1) / steps.length) * 100} 
                color="primary"
                className="max-w-full" 
            />
            <div className="flex items-center gap-2 mt-2">
                <div className="p-2 bg-primary/10 rounded-full text-primary">
                    {steps[currentStep].icon}
                </div>
                <h2 className="text-xl font-bold">{steps[currentStep].name}</h2>
            </div>
        </CardHeader>

      <CardBody>
          <Form {...form}>
        <form className="flex-1 flex flex-col h-full" onSubmit={form.handleSubmit(onSubmit)}>
            
            <ScrollShadow className="flex-1 p-2 -mx-2" hideScrollBar>
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={currentStep}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full"
                >
          {currentStep === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isAdmin && (
                <>
                  <Controller
                    control={form.control}
                    name="farmerID"
                    render={({ field }) => (
                      <Autocomplete
                        className="col-span-1 md:col-span-1"
                        errorMessage={form.formState?.errors?.farmerID?.message}
                        isInvalid={!!form.formState.errors.farmerID}
                        label="Select Farmer"
                        labelPlacement="outside"
                        placeholder="Search farmer..."
                        startContent={<User className="w-4 h-4 text-default-400" />}
                        items={farmers || []}
                        selectedKey={field.value}
                        onSelectionChange={field.onChange}
                      >
                        {(farmer: any) => (
                          <AutocompleteItem key={String(farmer._id)} textValue={`${farmer.firstname} ${farmer.lastname}`}>
                            <div className="flex flex-col">
                                <span className="text-small">{farmer.firstname} {farmer.lastname}</span>
                                <span className="text-tiny text-default-400">{farmer.email}</span>
                            </div>
                          </AutocompleteItem>
                        )}
                      </Autocomplete>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="farmID"
                    render={({ field }) => (
                      <Autocomplete
                        className="col-span-1 md:col-span-1"
                        errorMessage={form.formState?.errors?.farmID?.message}
                        isInvalid={!!form.formState.errors.farmID}
                        label="Select Farm"
                        labelPlacement="outside"
                        placeholder="Search farm..."
                        isDisabled={!selectedFarmerId}
                        items={filteredFarms || []}
                        selectedKey={field.value}
                        onSelectionChange={field.onChange}
                      >
                        {(farm: any) => (
                          <AutocompleteItem key={String(farm._id)} textValue={farm.farmName}>
                            {farm.farmName}
                          </AutocompleteItem>
                        )}
                      </Autocomplete>
                    )}
                  />
                </>
              )}
              
                <Controller
                  control={form.control}
                  defaultValue=""
                  name="gn"
                  render={({ field }) => (
                    <Input
                      {...field}
                      isClearable
                      isRequired
                      errorMessage={form.formState?.errors?.gn?.message}
                      isInvalid={!!form.formState?.errors?.gn}
                      label="Birth Registry ID"
                      labelPlacement="outside"
                      placeholder="e.g. 001"
                      startContent={<div className="pointer-events-none flex items-center"><span className="text-default-400 text-small">SG-</span></div>}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      {...field}
                      isClearable
                      isRequired
                      errorMessage={`${form.formState?.errors?.name?.message}`}
                      isInvalid={!!form.formState?.errors?.name}
                      label="Animal Name"
                      labelPlacement="outside"
                      placeholder="Name of The Animal"
                    />
                  )}
                />

                <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <I18nProvider locale="en-US">
                      <DatePicker
                        showMonthAndYearPickers
                        CalendarBottomContent={
                          <RadioGroup
                            aria-label="Date precision"
                            classNames={{
                              base: "w-full pb-2",
                              wrapper: "gap-2 px-2",
                            }}
                            defaultValue="exact_dates"
                            orientation="horizontal"
                          >
                            <CustomRadio value="unknown">Date Unknown</CustomRadio>
                          </RadioGroup>
                        }
                        CalendarTopContent={
                          <ButtonGroup
                            fullWidth
                            className="px-3 pb-2 pt-3 bg-content1"
                            radius="full"
                            size="sm"
                            variant="flat"
                          >
                            <Button onPress={() => setValue(now)}>Today</Button>
                            <Button onPress={() => setValue(nextWeek)}>Next week</Button>
                            <Button onPress={() => setValue(nextMonth)}>Next month</Button>
                          </ButtonGroup>
                        }
                        className="w-full"
                        label="Date of Birth"
                        labelPlacement="outside"
                        value={value}
                        onChange={setValue}
                      />
                    </I18nProvider>

                    <Controller
                        control={form.control}
                        defaultValue=""
                        name="coatColor"
                        render={({ field }) => (
                        <Select
                            {...field}
                            isRequired
                            errorMessage={form.formState?.errors?.coatColor?.message}
                            isInvalid={!!form.formState.errors.coatColor}
                            label="Coat Color"
                            labelPlacement="outside"
                            placeholder="Select color"
                        >
                            {coatColor.map((color) => (
                            <SelectItem key={color.label} startContent={<div className="w-4 h-4 rounded-full border border-default-200" style={{ backgroundColor: color.label.toLowerCase() }}></div>}>
                                {color.coatColor}
                            </SelectItem>
                            ))}
                        </Select>
                        )}
                    />
                </div>

                <Controller
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <Input
                      {...field}
                      isClearable
                      isRequired
                      errorMessage={`${form.formState?.errors?.age?.message}`}
                      isInvalid={!!form.formState?.errors?.age}
                      label="Age"
                      labelPlacement="outside"
                      placeholder="e.g. 2 years"
                      endContent={<span className="text-default-400 text-small">yrs</span>}
                    />
                  )}
                />

                <Controller
                  control={form.control}
                  defaultValue=""
                  name="animalStatus"
                  render={({ field }) => (
                    <Select
                      {...field}
                      isRequired
                      errorMessage={form.formState?.errors?.animalStatus?.message}
                      isInvalid={!!form.formState.errors.animalStatus}
                      label="Current Status"
                      labelPlacement="outside"
                      placeholder="Select status"
                    >
                      {statuses.map((stat) => (
                        <SelectItem key={stat.uid} textValue={stat.label}>
                           <div className="flex gap-2 items-center">
                                <div className={cn("w-2 h-2 rounded-full", stat.uid === "active" ? "bg-success" : "bg-warning")}></div>
                                {stat.label}
                           </div>
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  defaultValue=""
                  name="sex"
                  render={({ field }) => (
                    <Select
                      {...field}
                      isRequired
                      errorMessage={form.formState?.errors?.sex?.message}
                      isInvalid={!!form.formState.errors.sex}
                      label="Sex"
                      labelPlacement="outside"
                      placeholder="Select sex"
                    >
                      <SelectItem key={"male"} startContent={<span className="text-blue-500 font-bold">♂</span>}>Male</SelectItem>
                      <SelectItem key={"female"} startContent={<span className="text-pink-500 font-bold">♀</span>}>Female</SelectItem>
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="vaccination"
                  render={({ field }) => (
                    <Select
                      {...field}
                      errorMessage={form.formState?.errors?.vaccination?.message}
                      isInvalid={!!form.formState.errors.vaccination}
                      label="Vaccination History"
                      labelPlacement="outside"
                      placeholder="Select vaccines"
                      selectionMode="multiple"
                      startContent={<Syringe className="w-4 h-4 text-default-400" />}
                    >
                      {vaccineArray.map((vaccine) => (
                        <SelectItem key={vaccine.value}>{vaccine.label}</SelectItem>
                      ))}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <Input
                      {...field}
                      errorMessage={form.formState?.errors?.weight?.message}
                      isInvalid={!!form.formState.errors.weight}
                      label="Current Weight"
                      labelPlacement="outside"
                      placeholder="0.00"
                      type="number"
                      startContent={<Scale className="w-4 h-4 text-default-400" />}
                      endContent={<span className="text-default-400 text-small">kg</span>}
                    />
                  )}
                />

                <Controller
                  control={form.control}
                  defaultValue={undefined}
                  name="birthWeight"
                  render={({ field }) => (
                    <Input
                      {...field}
                      errorMessage={form.formState?.errors?.birthWeight?.message}
                      isInvalid={!!form.formState.errors.birthWeight}
                      label="Birth Weight"
                      labelPlacement="outside"
                      placeholder="0.00"
                      type="number"
                      startContent={<Scale className="w-4 h-4 text-default-400" />}
                      endContent={<span className="text-default-400 text-small">kg</span>}
                    />
                  )}
                />

                <Controller
                    control={form.control}
                    defaultValue=""
                    name="breedingType"
                    render={({ field }) => (
                        <Select
                        {...field}
                        isRequired
                        errorMessage={form.formState?.errors?.breedingType?.message}
                        isInvalid={!!form.formState.errors.breedingType}
                        label="Breeding Type"
                        labelPlacement="outside"
                        placeholder="Select type"
                        startContent={<HeartPulse className="w-4 h-4 text-default-400" />}
                        >
                        {breedingTypes.map((b) => (
                            <SelectItem key={b.name}>{b.label}</SelectItem>
                        ))}
                        </Select>
                    )}
                />

                <Controller
                    control={form.control}
                    defaultValue=""
                    name="size"
                    render={({ field }) => (
                        <Select
                        {...field}
                        isRequired
                        errorMessage={form.formState?.errors?.size?.message}
                        isInvalid={!!form.formState.errors.size}
                        label="Frame Size"
                        labelPlacement="outside"
                        placeholder="Select size"
                        startContent={<Ruler className="w-4 h-4 text-default-400" />}
                        >
                        {frameSizes.map((size) => (
                            <SelectItem key={size.value}>{size.label}</SelectItem>
                        ))}
                        </Select>
                    )}
                />
            
                <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-default-200 rounded-xl bg-content1/50">
                     <div className="col-span-1 md:col-span-2 text-sm font-semibold text-default-500">Genetics</div>
                    <Controller
                        control={form.control}
                        defaultValue=""
                        name="racialComposition"
                        render={({ field }) => (
                        <Select
                            {...field}
                            errorMessage={form.formState?.errors?.racialComposition?.message}
                            isInvalid={!!form.formState.errors.racialComposition}
                            label="Primary Breed"
                            labelPlacement="outside"
                            placeholder="Select breed"
                        >
                            {nigerianBreeds.map((breed) => (
                            <SelectItem key={breed.breed}>{breed.label}</SelectItem>
                            ))}
                        </Select>
                        )}
                    />
                    <Controller
                        control={form.control}
                        defaultValue=""
                        name="breedCompositionRatio"
                        render={({ field }) => (
                        <Select
                            {...field}
                            isRequired
                            errorMessage={form.formState?.errors?.breedCompositionRatio?.message}
                            isInvalid={!!form.formState.errors.breedCompositionRatio}
                            label="Composition Ratio"
                            labelPlacement="outside"
                            placeholder="Select ratio"
                        >
                            {ratios.map((ratio: any) => (
                            <SelectItem key={ratio.value}>{ratio.value}</SelectItem>
                            ))}
                        </Select>
                        )}
                    />
                </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="flex flex-col gap-6">
                
              {/* Direct Parents Card */}
              <Card shadow="sm" className="bg-default-50 border border-default-200">
                <CardBody className="gap-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1 bg-primary/20 rounded-md text-primary"><User className="w-4 h-4"/></div>
                    <h3 className="font-semibold text-lg">Direct Parents</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Sire Column */}
                      <div className="space-y-3">
                          <span className="text-sm font-medium text-primary">Sire (Father)</span>
                          <Controller
                            control={form.control}
                            name="sireName"
                            render={({ field }) => (
                                <Input {...field} isClearable isRequired size="sm" label="Name" placeholder="Sire's Name" errorMessage={form.formState?.errors?.sireName?.message} isInvalid={!!form.formState?.errors?.sireName} />
                            )}
                            />
                            <Controller
                                control={form.control}
                                name="sireDGR"
                                render={({ field }) => (
                                    <Input {...field} isClearable isRequired size="sm" label="DGR" placeholder="Sire's DGR" errorMessage={form.formState?.errors?.sireDGR?.message} isInvalid={!!form.formState?.errors?.sireDGR} />
                                )}
                            />
                            <Controller
                                control={form.control}
                                defaultValue=""
                                name="sireBreed"
                                render={({ field }) => (
                                    <Select {...field} size="sm" label="Breed" placeholder="Select Breed">
                                        {nigerianBreeds.map((breed) => (<SelectItem key={breed.breed}>{breed.label}</SelectItem>))}
                                    </Select>
                                )}
                            />
                      </div>

                       {/* Dam Column */}
                       <div className="space-y-3">
                          <span className="text-sm font-medium text-pink-500">Dam (Mother)</span>
                          <Controller
                            control={form.control}
                            name="damName"
                            render={({ field }) => (
                                <Input {...field} isClearable isRequired size="sm" label="Name" placeholder="Dam's Name" errorMessage={form.formState?.errors?.damName?.message} isInvalid={!!form.formState?.errors?.damName} />
                            )}
                            />
                            <Controller
                                control={form.control}
                                name="damDGR"
                                render={({ field }) => (
                                    <Input {...field} isClearable isRequired size="sm" label="DGR" placeholder="Dam's DGR" errorMessage={form.formState?.errors?.damDGR?.message} isInvalid={!!form.formState?.errors?.damDGR} />
                                )}
                            />
                            <Controller
                                control={form.control}
                                defaultValue=""
                                name="damBreed"
                                render={({ field }) => (
                                    <Select {...field} size="sm" label="Breed" placeholder="Select Breed">
                                        {nigerianBreeds.map((breed) => (<SelectItem key={breed.breed}>{breed.label}</SelectItem>))}
                                    </Select>
                                )}
                            />
                      </div>
                  </div>
                </CardBody>
              </Card>

              {/* Grandparents Accordion */}
              <Accordion variant="splitted" selectionMode="multiple">
                <AccordionItem 
                    key="1" 
                    aria-label="Paternal Grandparents" 
                    title="Paternal Grandparents" 
                    subtitle="Sire's Parents"
                    startContent={<Dna className="text-primary"/>}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                        <div className="space-y-2 p-2 bg-default-50 rounded-lg">
                            <span className="text-xs font-bold uppercase text-default-500">Grandfather</span>
                            <Controller control={form.control} name="paternalGrandSireName" render={({ field }) => (<Input {...field} size="sm" label="Name" placeholder="Grand Sire Name" />)} />
                            <Controller control={form.control} name="paternalGrandSireDGR" render={({ field }) => (<Input {...field} size="sm" label="DGR" placeholder="Grand Sire DGR" />)} />
                            <Controller control={form.control} name="paternalGrandSireBreed" render={({ field }) => (<Select {...field} size="sm" label="Breed">{nigerianBreeds.map((b) => (<SelectItem key={b.breed}>{b.label}</SelectItem>))}</Select>)} />
                        </div>
                        <div className="space-y-2 p-2 bg-default-50 rounded-lg">
                            <span className="text-xs font-bold uppercase text-default-500">Grandmother</span>
                            <Controller control={form.control} name="paternalGrandDamName" render={({ field }) => (<Input {...field} size="sm" label="Name" placeholder="Grand Dam Name" />)} />
                            <Controller control={form.control} name="paternalGrandDamDGR" render={({ field }) => (<Input {...field} size="sm" label="DGR" placeholder="Grand Dam DGR" />)} />
                            <Controller control={form.control} name="paternalGrandDamBreed" render={({ field }) => (<Select {...field} size="sm" label="Breed">{nigerianBreeds.map((b) => (<SelectItem key={b.breed}>{b.label}</SelectItem>))}</Select>)} />
                        </div>
                    </div>
                </AccordionItem>
                <AccordionItem 
                    key="2" 
                    aria-label="Maternal Grandparents" 
                    title="Maternal Grandparents" 
                    subtitle="Dam's Parents"
                    startContent={<Dna className="text-pink-500"/>}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                        <div className="space-y-2 p-2 bg-default-50 rounded-lg">
                            <span className="text-xs font-bold uppercase text-default-500">Grandfather</span>
                            <Controller control={form.control} name="maternalGrandSireName" render={({ field }) => (<Input {...field} size="sm" label="Name" placeholder="Grand Sire Name" />)} />
                            <Controller control={form.control} name="maternalGrandSireDGR" render={({ field }) => (<Input {...field} size="sm" label="DGR" placeholder="Grand Sire DGR" />)} />
                            <Controller control={form.control} name="maternalGrandSireBreed" render={({ field }) => (<Select {...field} size="sm" label="Breed">{nigerianBreeds.map((b) => (<SelectItem key={b.breed}>{b.label}</SelectItem>))}</Select>)} />
                        </div>
                        <div className="space-y-2 p-2 bg-default-50 rounded-lg">
                            <span className="text-xs font-bold uppercase text-default-500">Grandmother</span>
                            <Controller control={form.control} name="maternalGrandDamName" render={({ field }) => (<Input {...field} size="sm" label="Name" placeholder="Grand Dam Name" />)} />
                            <Controller control={form.control} name="maternalGrandDamDGR" render={({ field }) => (<Input {...field} size="sm" label="DGR" placeholder="Grand Dam DGR" />)} />
                            <Controller control={form.control} name="maternalGrandDamBreed" render={({ field }) => (<Select {...field} size="sm" label="Breed">{nigerianBreeds.map((b) => (<SelectItem key={b.breed}>{b.label}</SelectItem>))}</Select>)} />
                        </div>
                    </div>
                </AccordionItem>
              </Accordion>
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex flex-col w-full gap-6">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    control={form.control}
                    defaultValue=""
                    name="paternalType"
                    render={({ field }) => (
                      <Select
                        {...field}
                        errorMessage={form.formState?.errors?.paternalType?.message}
                        isInvalid={!!form.formState?.errors?.paternalType}
                        label="Paternal Lineage Type"
                        labelPlacement="outside"
                        placeholder="Select type"
                      >
                        {paternalTypes.map((pattern) => (
                          <SelectItem key={pattern.value} textValue={pattern.label}>{pattern.label}</SelectItem>
                        ))}
                      </Select>
                    )}
                  />
                  <Controller
                    control={form.control}
                    defaultValue=""
                    name="birthCondition"
                    render={({ field }) => (
                      <Select
                        {...field}
                        errorMessage={form.formState?.errors?.birthCondition?.message}
                        isInvalid={!!form.formState?.errors?.birthCondition}
                        label="Birth Condition"
                        labelPlacement="outside"
                        placeholder="Select condition"
                      >
                        {birthConditions.map((pattern) => (
                          <SelectItem key={pattern.uid} textValue={pattern.label}>{pattern.label}</SelectItem>
                        ))}
                      </Select>
                    )}
                  />
                </div>
                
                <Divider/>

                <div className="flex flex-col md:flex-row gap-8 items-start">
                    {/* Image Drag and Drop Area */}
                    <div 
                        className={cn(
                            "flex-1 w-full h-64 border-2 border-dashed rounded-2xl flex flex-col justify-center items-center gap-4 transition-all duration-300 relative overflow-hidden group",
                            isDragging ? "border-primary bg-primary/5" : "border-default-300 hover:bg-default-100",
                            preview ? "border-none" : ""
                        )}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {preview ? (
                             <div className="relative w-full h-full">
                                <Image
                                    alt="Animal Preview"
                                    classNames={{
                                        wrapper: "w-full h-full",
                                        img: "w-full h-full object-cover"
                                    }}
                                    src={preview}
                                    removeWrapper
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button color="danger" variant="flat" startContent={<Trash2 size={18}/>} onPress={() => { setImg(undefined); setPreview(null); }}>Remove Image</Button>
                                </div>
                             </div>
                        ) : (
                            <>
                                <div className="p-4 rounded-full bg-default-100 text-default-500">
                                    <FileUp size={40} />
                                </div>
                                <div className="text-center">
                                    <p className="font-semibold text-lg">Drag & Drop Image here</p>
                                    <p className="text-sm text-default-400">or click to browse</p>
                                </div>
                                <Button size="sm" variant="flat" onPress={() => fileInputRef.current?.click()}>Browse Files</Button>
                                <Input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </>
                        )}
                    </div>
                    
                    {/* Info Card side of upload */}
                    <div className="flex-1 w-full space-y-4">
                        <Card className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary">
                            <CardBody>
                                <h4 className="font-bold flex items-center gap-2"><CheckCircle2 size={18}/> Requirements</h4>
                                <ul className="list-disc list-inside text-sm mt-2 space-y-1 text-default-600">
                                    <li>Clear visibility of animal markings.</li>
                                    <li>If branded, brand ID must be visible.</li>
                                    <li>Good lighting conditions.</li>
                                </ul>
                            </CardBody>
                        </Card>
                        {img && (
                            <div className="flex items-center gap-3 p-3 border border-default-200 rounded-lg">
                                <div className="p-2 bg-default-100 rounded-lg"><Image src={preview || ""} width={40} height={40} className="rounded object-cover"/></div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold truncate">{img.name}</p>
                                    <p className="text-xs text-default-400">{(img.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                                <Chip size="sm" color="success" variant="flat">Ready</Chip>
                            </div>
                        )}
                    </div>
                </div>
            </div>
          )}
             </motion.div>
            </AnimatePresence>
            </ScrollShadow>
            
            {/* Footer Buttons */}
            <CardFooter className="pt-4 border-t border-default-100 flex justify-between items-center mt-auto">
              {currentStep > 0 ? (
                  <Button
                    color="default"
                    variant="light"
                    onPress={prev}
                    startContent={<ArrowLeft size={16}/>}
                  >
                    Back
                  </Button>
              ) : (
                  <div/> /* Spacer */
              )}

              {currentStep < steps.length - 1 ? (
                  <Button
                    color="primary"
                    variant="shadow"
                    onPress={next}
                    endContent={<ArrowRight size={16}/>}
                  >
                    Next Step
                  </Button>
              ) : (
                <Button
                  color="primary"
                  variant="shadow"
                  type="submit"
                  isLoading={isPending}
                  onPress={onClose}
                  startContent={!isPending && <CheckCircle2 size={18}/>}
                >
                  Confirm Registration
                </Button>
              )}
            </CardFooter>
        </form> 
      </Form>
    </CardBody>
    </Card>
  );
};

export default AnimalForm;
